import type { MessageKey } from '@dijitalapp/i18n';
import {
  SITE_HEADING_FONTS,
  SITE_HERO_ALIGNS,
  SITE_HERO_VISUALS,
  SITE_PRESET_DEFAULTS,
  SITE_PRESETS,
  SITE_RADII,
  type AdminSiteAppearanceDto,
  type SiteAppearance,
  type SitePreset,
  type SiteSection,
} from '@dijitalapp/shared';
import { useEffect, useRef, useState } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';

/*
 * Admin: how dijitalapp.com looks. Pick one of three themes, then adjust colours, heading font, corners,
 * the hero layout and the order of the sections. Every change is previewed live in the frame on the
 * right (a short-lived draft, not published); "Save and publish" makes it the site, with history.
 */

type ColourKey = keyof SiteAppearance['colors'];
const COLOURS: ColourKey[] = ['accent', 'secondary', 'background', 'text'];
type Device = 'desktop' | 'tablet' | 'phone';
const DEVICE_WIDTH: Record<Device, number> = { desktop: 1280, tablet: 768, phone: 390 };

export function SiteAppearanceEditor() {
  const { i18n } = useApp();
  const { t, formatDate } = i18n;
  const [data, setData] = useState<AdminSiteAppearanceDto | null>(null);
  const [draft, setDraft] = useState<SiteAppearance | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [locale, setLocale] = useState<'tr' | 'en'>('tr');
  const [dark, setDark] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setStageSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  useEffect(() => {
    void api<AdminSiteAppearanceDto>('GET', '/admin/site-appearance').then((d) => {
      setData(d);
      setDraft(d.appearance);
    });
  }, []);

  // Live preview: a fresh draft shortly after each change (the server keeps drafts for 15 minutes).
  useEffect(() => {
    if (!draft) return;
    let alive = true;
    const timer = window.setTimeout(() => {
      void api<{ url: string }>('POST', '/admin/site-appearance/preview', { appearance: draft, locale }).then(
        (r) => alive && setPreviewUrl(`${r.url}${dark ? '?theme=dark' : ''}`),
        () => undefined,
      );
    }, 450);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [draft, locale, dark]);

  if (!data || !draft) return <p className="muted">{t('app.loading')}</p>;
  // The site is drawn at the device's real width and scaled down to fit, so "Desktop" shows the desktop layout.
  const frameWidth = DEVICE_WIDTH[device];
  const scale = stageSize.width ? Math.min(1, (stageSize.width - 16) / frameWidth) : 1;
  const frameHeight = stageSize.height ? Math.round((stageSize.height - 16) / scale) : 900;
  const dirty = JSON.stringify(draft) !== JSON.stringify(data.appearance);
  const presetDefaults = SITE_PRESET_DEFAULTS[draft.preset];
  const set = <K extends keyof SiteAppearance>(k: K, v: SiteAppearance[K]) => setDraft((d) => ({ ...d!, [k]: v }));
  const setColour = (k: ColourKey, v: string) => setDraft((d) => ({ ...d!, colors: { ...d!.colors, [k]: v } }));
  const move = (from: number, to: number) => {
    if (to < 0 || to >= draft.sectionOrder.length) return;
    const order = draft.sectionOrder.slice();
    const [x] = order.splice(from, 1);
    order.splice(to, 0, x!);
    set('sectionOrder', order);
  };
  const run = (fn: () => Promise<AdminSiteAppearanceDto>, ok: string) => {
    setBusy(true);
    setStatus(null);
    fn()
      .then(
        (d) => {
          setData(d);
          setDraft(d.appearance);
          setStatus({ ok: true, text: ok });
        },
        (err: unknown) => setStatus({ ok: false, text: t(errorMessageKey(err)) }),
      )
      .finally(() => setBusy(false));
  };
  const save = () => run(() => api('PUT', '/admin/site-appearance', { appearance: draft, expectedVersion: data.version }), t('appearance.saved'));
  const restore = (historyId: string) => run(() => api('POST', '/admin/site-appearance/restore', { historyId, expectedVersion: data.version }), t('appearance.restored'));

  const segmented = <T extends string>(label: string, value: T, options: readonly T[], onChange: (v: T) => void, key: string) => (
    <div className="field">
      <span className="field-label">{label}</span>
      <div className="segmented segmented-wrap" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button key={o} type="button" role="radio" aria-checked={value === o} onClick={() => onChange(o)}>
            {t(`appearance.${key}.${o}` as MessageKey)}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="appearance-layout">
      <div className="appearance-controls">
        <div className="card stack appearance-actions">
          <div className="row">
            <span className="muted">
              {data.version
                ? t('adminOps.site.version', { version: data.version, by: data.updatedBy ? `@${data.updatedBy}` : '—', date: formatDate(data.updatedAt!, { dateStyle: 'medium', timeStyle: 'short' }) })
                : t('appearance.defaultsLive')}
            </span>
          </div>
          <div className="row">
            <button type="button" className="btn btn-sm" disabled={!dirty || busy} onClick={() => setDraft(data.appearance)}>
              {t('adminOps.site.undo')}
            </button>
            <span className="spacer" />
            <button type="button" className="btn btn-primary btn-sm" disabled={!dirty || busy} onClick={save}>
              {t('adminOps.site.save')}
            </button>
          </div>
          {status && (
            <p role="status" className={status.ok ? 'alert alert-info' : 'alert alert-error'}>
              {status.text}
            </p>
          )}
        </div>

        <section className="card stack">
          <h2 className="card-title">{t('appearance.theme')}</h2>
          <div className="preset-grid" role="radiogroup" aria-label={t('appearance.theme')}>
            {SITE_PRESETS.map((p: SitePreset) => {
              const d = SITE_PRESET_DEFAULTS[p];
              return (
                <button key={p} type="button" role="radio" aria-checked={draft.preset === p} className="preset-card" onClick={() => set('preset', p)}>
                  <span className="preset-swatch" aria-hidden>
                    {[d.background, d.text, d.accent, d.secondary].map((c) => (
                      <i key={c} style={{ background: c }} />
                    ))}
                  </span>
                  <strong>{t(`appearance.preset.${p}` as MessageKey)}</strong>
                  <span className="muted">{t(`appearance.presetDesc.${p}` as MessageKey)}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="card stack">
          <h2 className="card-title">{t('appearance.colours')}</h2>
          <p className="hint">{t('appearance.coloursHint')}</p>
          {COLOURS.map((k) => {
            const value = draft.colors[k] || presetDefaults[k];
            return (
              <div key={k} className="colour-row">
                <input id={`col-${k}`} type="color" value={value} onChange={(e) => setColour(k, e.target.value)} aria-label={t(`appearance.colour.${k}` as MessageKey)} />
                <label htmlFor={`col-${k}`}>
                  <strong>{t(`appearance.colour.${k}` as MessageKey)}</strong>
                  <span className="muted">
                    {value.toUpperCase()} · {draft.colors[k] ? t('appearance.custom') : t('appearance.fromTheme')}
                  </span>
                </label>
                {draft.colors[k] && (
                  <button type="button" className="btn btn-sm" onClick={() => setColour(k, '')}>
                    {t('appearance.useTheme')}
                  </button>
                )}
              </div>
            );
          })}
        </section>

        <section className="card stack">
          <h2 className="card-title">{t('appearance.shape')}</h2>
          {segmented(t('appearance.headingFont'), draft.headingFont, SITE_HEADING_FONTS, (v) => set('headingFont', v), 'font')}
          {segmented(t('appearance.radius'), draft.radius, SITE_RADII, (v) => set('radius', v), 'radiusOpt')}
        </section>

        <section className="card stack">
          <h2 className="card-title">{t('appearance.hero')}</h2>
          {segmented(t('appearance.heroVisual'), draft.heroVisual, SITE_HERO_VISUALS, (v) => set('heroVisual', v), 'visual')}
          {segmented(t('appearance.heroAlign'), draft.heroAlign, SITE_HERO_ALIGNS, (v) => set('heroAlign', v), 'align')}
        </section>

        <section className="card stack">
          <h2 className="card-title">{t('appearance.order')}</h2>
          <p className="hint">{t('appearance.orderHint')}</p>
          <ol className="order-list">
            {draft.sectionOrder.map((sec: SiteSection, i) => (
              <li key={sec}>
                <span>{t(`adminOps.site.section.${sec}` as MessageKey)}</span>
                <span className="spacer" />
                <button type="button" className="btn btn-sm" aria-label={t('adminOps.site.up')} disabled={i === 0} onClick={() => move(i, i - 1)}>
                  ↑
                </button>
                <button type="button" className="btn btn-sm" aria-label={t('adminOps.site.down')} disabled={i === draft.sectionOrder.length - 1} onClick={() => move(i, i + 1)}>
                  ↓
                </button>
              </li>
            ))}
          </ol>
        </section>

        <section className="card stack">
          <h2 className="card-title">{t('adminOps.site.history')}</h2>
          {data.history.length === 0 && <p className="muted">{t('adminOps.site.noHistory')}</p>}
          {data.history.map((h) => (
            <div key={h.id} className="site-history-row">
              <div>
                <strong>v{h.version}</strong>
                <p className="muted">
                  {formatDate(h.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  {h.createdBy ? ` · @${h.createdBy}` : ''}
                </p>
              </div>
              {h.version !== data.version && (
                <button type="button" className="btn btn-sm" disabled={busy} onClick={() => restore(h.id)}>
                  {t('adminOps.site.restore')}
                </button>
              )}
            </div>
          ))}
        </section>
      </div>

      <section className="card appearance-preview" aria-label={t('appearance.preview')}>
        <div className="row preview-toolbar">
          <strong>{t('appearance.preview')}</strong>
          {dirty && <span className="badge">{t('appearance.unsaved')}</span>}
          <span className="spacer" />
          <div className="segmented" role="radiogroup" aria-label={t('appearance.device')}>
            {(['desktop', 'tablet', 'phone'] as const).map((d) => (
              <button key={d} type="button" role="radio" aria-checked={device === d} onClick={() => setDevice(d)}>
                {t(`appearance.deviceOpt.${d}` as MessageKey)}
              </button>
            ))}
          </div>
          <div className="segmented" role="radiogroup" aria-label={t('adminOps.site.language')}>
            {(['tr', 'en'] as const).map((l) => (
              <button key={l} type="button" role="radio" aria-checked={locale === l} onClick={() => setLocale(l)}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <button type="button" className="btn btn-sm" aria-pressed={dark} onClick={() => setDark((x) => !x)}>
            {dark ? t('settings.theme.light') : t('settings.theme.dark')}
          </button>
        </div>
        <div className="preview-stage" ref={stageRef}>
          {previewUrl ? (
            <div className="preview-scaler" style={{ width: frameWidth * scale, height: frameHeight * scale }}>
              <iframe
                title={t('appearance.preview')}
                src={previewUrl}
                style={{ width: frameWidth, height: frameHeight, transform: `scale(${scale})` }}
                sandbox="allow-scripts allow-same-origin"
                referrerPolicy="no-referrer"
              />
            </div>
          ) : (
            <p className="muted">{t('app.loading')}</p>
          )}
        </div>
        <p className="hint">{t('appearance.previewHint')}</p>
      </section>
    </div>
  );
}
